import {
  UseBaseMutationResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { thcApi } from "./apiClient";
import { getNextPageParam } from "@/lib/utils";
import useAuthentication from "@/hooks/useAuthentication";
import {
  DeliverableSubmissionCreateParams,
  GetProgramsParams,
  GetRecentFundedApplicationsParams,
  GetSubmissionsParams,
  GrantApplicationDeliverable,
  GrantApplicationProjectMetadata,
  GrantApplicationReview,
} from "@thrivecoin/adapter/dist/types";
import queryClient from "@/lib/reactQuery";
import { useMemo } from "react";
import { ReviewSubmissionParams } from "@thrivecoin/adapter/dist/types/submissions/requestParams";

export function useShowGrants(params?: GetProgramsParams) {
  return useInfiniteQuery({
    queryKey: ["grants-show", params],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.grants({ page: pageParam, ...params }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowOverallStats() {
  return useQuery({
    queryKey: ["overall-stats"],
    queryFn: () => thcApi.grantsApp.overallStats(),
  });
}

export function useGrantDetails(id: string) {
  return useQuery({
    queryKey: ["grant-details", id],
    queryFn: () => thcApi.grantsApp.grantDetails(id),
  });
}

export function useShowStats(communityId?: number) {
  return useQuery({
    queryKey: ["stats", communityId],
    queryFn: () => thcApi.grantsApp.stats(communityId),
  });
}

export function useShowGrantApplication(id: number | string) {
  const { user } = useAuthentication();
  return useQuery({
    queryKey: ["grant-status", id],
    queryFn: () => thcApi.grantsApp.grantApplication(id),
    enabled: !!user && !!id,
  });
}

export function useShowDeliverables(params?: GetRecentFundedApplicationsParams) {
  return useInfiniteQuery({
    queryKey: ["deliverables", params],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.deliverables({ page: pageParam, ...params }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-project"],
    mutationFn: (params: Record<string, unknown>) => {
      return thcApi.grantsApp.createProject(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useGetProjects() {
  return useInfiniteQuery({
    queryKey: ["projects"],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.projects({ page: pageParam }),
    initialPageParam: 1,
    getNextPageParam,
    staleTime: 1000 * 60 * 60,
  });
}

export function useGetMyProjects() {
  return useInfiniteQuery({
    queryKey: ["my-projects"],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.getMyProjects({ page: pageParam }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useGetProjectInfo(
  id: number,
  options: Partial<UseQueryOptions<GrantApplicationProjectMetadata>> = { enabled: true },
) {
  return useQuery({
    queryKey: [`project-info-${id}`],
    queryFn: () => thcApi.grantsApp.getProjectInfo(id),
    staleTime: 1000 * 60 * 60,
    ...options,
  });
}

export function useUpdateProject(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: Record<string, unknown>) => {
      return thcApi.grantsApp.updateProject(id, params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`project-info-${id}`] });
      queryClient.invalidateQueries({ queryKey: ["my-projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useGetGrantAdditionalQuestions(id: string | number) {
  return useQuery({
    queryKey: [`grant-additional-questions-${id}`],
    queryFn: () => thcApi.grantsApp.grantAdditionalQuestions(id),
    staleTime: 1000 * 60 * 60,
  });
}

export function useCreateGrantApplication(
  grantId: number | string,
): UseBaseMutationResult<void, unknown, Parameters<typeof thcApi.grantsApp.createGrantApplication>[1]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-grant-application", grantId],
    mutationFn: (params) => {
      return thcApi.grantsApp.createGrantApplication(grantId, params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grant-status", grantId] });
    },
  });
}

export function useShowGrantApplications(id: number) {
  const status = "approved";

  return useInfiniteQuery({
    queryKey: ["grant-applications", id],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.grantApplications({ params: { page: pageParam, status }, id }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useGrantApplicationDeliverables(
  id: number,
  options: Partial<UseQueryOptions<GrantApplicationDeliverable[]>> = { enabled: true },
  status?: string,
) {
  const { user } = useAuthentication();
  return useQuery({
    queryKey: [`grant-application-deliverables-${id}`, status],
    queryFn: () => thcApi.grantsApp.grantApplicationDeliverables(id, status),
    enabled: !!user && options.enabled,
    ...options,
  });
}

export function useShowCurrentUser() {
  const { user } = useAuthentication();
  return useQuery({
    queryKey: ["show-current-user"],
    queryFn: () => thcApi.grantsApp.currentUser(),
    enabled: !!user?.id,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: { username: string; description: string; image?: File | null }) => {
      const { username, description, image } = data;
      return thcApi.grantsApp.updateProfile(username, description, image);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["show-current-user"] }),
  });
  return mutation;
}

export function useFundedDeliverables(params = {}) {
  return useInfiniteQuery({
    queryKey: ["funded-deliverables", params],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.fundedDeliverables({ page: pageParam, ...params }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useApprovedGrantApplications(params = {}) {
  return useInfiniteQuery({
    queryKey: ["approve-grant-applications", params],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.approvedGrantApplications({ page: pageParam, ...params }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ["user-stats"],
    queryFn: () => thcApi.grantsApp.userStats(),
  });
}

export function useShowRewards() {
  return useInfiniteQuery({
    queryKey: ["rewards"],
    queryFn: ({ pageParam = 1 }) => {
      const per_page = 20;
      return thcApi.grantsApp.rewards({ page: pageParam, per_page });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowCommunities(params: { per_page?: number } = {}) {
  const { user } = useAuthentication();

  const queryKey = useMemo(() => {
    if (!user?.id) return ["communities", null];
    return ["communities", user.id, params];
  }, [user?.id, params]);

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) => {
      const per_page = params.per_page || 20;
      return thcApi.grantsApp.communities({ page: pageParam, per_page });
    },
    initialPageParam: 1,
    getNextPageParam,
    enabled: !!user?.id,
  });
}

export function useGetSubmissions(overlapCountRef: React.MutableRefObject<number>, params: GetSubmissionsParams = {}) {
  return useInfiniteQuery({
    queryKey: ["submissions", params],
    queryFn: ({ pageParam = 1 }) => {
      if (!pageParam || pageParam < 2) {
        overlapCountRef.current = 0;
      }

      return thcApi.grantsApp.getSubmissions({ page: pageParam, ...params, overlapCount: overlapCountRef?.current });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowRecommendedContributions() {
  return useInfiniteQuery({
    queryKey: ["recommended-contributions"],
    queryFn: ({ pageParam = 1 }) => {
      const per_page = pageParam === 1 ? 3 : 20;
      return thcApi.grantsApp.getRecommendedContributions({ page: pageParam, per_page });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowRecommendedGrants() {
  return useInfiniteQuery({
    queryKey: ["recommended-grants"],
    queryFn: ({ pageParam = 1 }) => {
      const per_page = pageParam === 1 ? 2 : 20;
      return thcApi.grantsApp.getRecommendedGrants({ page: pageParam, per_page });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowAdditionalWallets(params: Parameters<typeof thcApi.grantsApp.getAdditionalWallets>[0] = {}) {
  return useQuery({
    queryKey: ["additional-wallets", params],
    queryFn: () => thcApi.grantsApp.getAdditionalWallets(params),
  });
}

export function useDeleteAdditionalWallet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => thcApi.grantsApp.deleteAdditionalWallet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["additional-wallets"] });
    },
  });
}

export function useShowActivityContributions(status: string, isActive: boolean = false) {
  return useInfiniteQuery({
    queryKey: ["activity-contributions"],
    enabled: isActive,
    queryFn: ({ pageParam = 1 }) => {
      const per_page = pageParam === 1 ? 3 : 20;
      return thcApi.grantsApp.getCurrentUserContributions({ page: pageParam, per_page, status });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowActivityGrants(application_status: string, isActive: boolean = false) {
  return useInfiniteQuery({
    queryKey: ["activity-grants"],
    enabled: isActive,
    queryFn: ({ pageParam = 1 }) => {
      const per_page = pageParam === 1 ? 3 : 20;
      return thcApi.grantsApp.getCurrentUserGrants({ page: pageParam, per_page, application_status });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useShowActivityReviews(review_status: string, isActive: boolean = false) {
  return useInfiniteQuery({
    queryKey: ["activity-reviews"],
    enabled: isActive,
    queryFn: ({ pageParam = 1 }) => {
      const per_page = pageParam === 1 ? 3 : 20;
      return thcApi.grantsApp.getCurrentUserReviews({ page: pageParam, per_page, review_status });
    },
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useFollowCommunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: Parameters<typeof thcApi.grantsApp.followCommunity>[0]) =>
      thcApi.grantsApp.followCommunity(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communities"] });
      queryClient.invalidateQueries({ queryKey: ["grants-show"] });
    },
  });
}

export function useUnfollowCommunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: Parameters<typeof thcApi.grantsApp.unfollowCommunity>[0]) =>
      thcApi.grantsApp.unfollowCommunity(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["communities"] });
    },
  });
}

export function useGrantApplicationReviews(
  id: string | number,
  options: Partial<UseQueryOptions<GrantApplicationReview[]>> = {},
) {
  return useQuery({
    queryKey: ["grant-application-reviews"],
    queryFn: () => thcApi.grantsApp.getGrantApplicationReviews(id),
    ...options,
  });
}

export function useReviewConfig(id: number | string) {
  return useQuery({
    queryKey: ["review-config", id],
    queryFn: () => thcApi.grantsApp.getReviewConfig(id),
  });
}

export function useReviewSubmission() {
  return useMutation({
    mutationKey: ["review-submission"],
    mutationFn: (params: ReviewSubmissionParams) => {
      return thcApi.grantsApp.reviewSubmission(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["submissions"] });
    },
  });
}

export function useShowSubmission(id: number | string) {
  return useQuery({
    queryKey: ["submission", id],
    queryFn: () => thcApi.grantsApp.getSubmission(id),
    enabled: !!id,
  });
}

export function useReferralStats() {
  return useQuery({
    queryKey: ["referral-stats"],
    queryFn: () => thcApi.grantsApp.getReferralStats(),
  });
}

export function useReferredApplications() {
  return useInfiniteQuery({
    queryKey: ["referred-applications"],
    queryFn: ({ pageParam }) => thcApi.grantsApp.getReferredApplications({ page: pageParam }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useUserNotifications() {
  const { user } = useAuthentication();

  return useInfiniteQuery({
    queryKey: ["user_notifications", user?.id],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.userNotifications(pageParam),
    initialPageParam: 1,
    getNextPageParam: getNextPageParam,
    enabled: !!user?.id,
  });
}

export function useMarkAsSeen() {
  return useMutation({
    mutationFn: async (id: number) => thcApi.grantsApp.markAsSeen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_notifications"] });
    },
  });
}

export function useActiveGrantApplications(params = {}) {
  return useInfiniteQuery({
    queryKey: ["active-grant-applications", params],
    queryFn: ({ pageParam = 1 }) => thcApi.grantsApp.activeGrantApplications({ page: pageParam, ...params }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

export function useGetReviewStats() {
  return useQuery({
    queryKey: ["review-stats"],
    queryFn: () => thcApi.grantsApp.getReviewStats(),
  });
}

export const useGetHistoryReviews = (
  enabled: boolean,
  params: { sort_type: string; sort_direction: string; keyword?: string },
) => {
  return useInfiniteQuery({
    queryKey: ["history-reviews", params],
    enabled,
    queryFn: ({ pageParam = 1 }) =>
      thcApi.grantsApp.getCurrentUserReviews({
        page: pageParam,
        per_page: 20,
        ...params,
      }),
    initialPageParam: 1,
    getNextPageParam,
  });
};

export function useGetValidation(id?: string | number) {
  return useQuery({
    queryKey: ["review-stats", id],
    queryFn: () => thcApi.grantsApp.getHumanValidation(id!),
    enabled: !!id,
  });
}

export function useDeliverableSubmissionShow(id: number | string) {
  return useQuery({
    queryKey: [`deliverable-submission-show-${id}`],
    queryFn: () => thcApi.grantsApp.deliverableSubmissionShow(id),
  });
}

export function useCreateDeliverableSubmission(id: number | string) {
  return useMutation({
    mutationFn: (params: DeliverableSubmissionCreateParams) => {
      return thcApi.grantsApp.deliverableSubmissionCreate(id, params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`grant-application-deliverables-${id}`] });
      queryClient.invalidateQueries({ queryKey: [`deliverable-submission-show-${id}`] });
    },
  });
}

export function useGetSumsubAccessTokenData() {
  return useQuery({
    queryKey: ["sumsub-access-token"],
    queryFn: () => thcApi.grantsApp.getSumsubAccessTokenData(),
  });
}

export function useGetKYCVerificationData() {
  return useQuery({
    queryKey: ["kyc-verification-data"],
    queryFn: () => thcApi.grantsApp.getKYCVerificationData(),
  });
}

export function useUpdateReferralCode() {
  return useMutation({
    mutationFn: (referral_code: string) => thcApi.grantsApp.updateReferralCode(referral_code),
  });
}

export function useGetGrantPredefinedMilestones(id: number) {
  return useQuery({
    queryKey: ["grant-predefined-milestones", id],
    queryFn: () => thcApi.grantsApp.getGrantPredefinedMilestones(id),
  });
}

export function useRecommendedPrograms(program_id: number | null) {
  return useQuery({
    queryKey: ["recommended-programs", program_id],
    queryFn: () => {
      if (!program_id) return;
      return thcApi.grantsApp.getRecommendedPrograms(program_id);
    },
    enabled: !!program_id,
  });
}

export function useConnectFarcaster() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (signature_data: {
      message: string;
      signature: string;
      domain: string;
      fid: number;
      username: string;
      pfpUrl: string;
    }) => {
      const payload = {
        provider: "farcaster",
        signature_data,
      };
      return thcApi.grantsApp.connectSocialProfile(payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["show-current-user"] }),
  });
  return mutation;
}

export function useDisconnectNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => thcApi.grantsApp.disconnectSocial(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["show-current-user"] });
    },
  });
}

export const useUpdateRewardAddress = (grantApplicationId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (address: string) => {
      return thcApi.grantsApp.updateRewardAddress({
        grantApplicationId,
        address,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["active-grant-applications"] });
      queryClient.invalidateQueries({ queryKey: [`grant-application-deliverables-${grantApplicationId}`] });
    },
  });
};

export const useGetMilestonePaymentDetails = (milestoneId: number) => {
  return useQuery({
    queryKey: ["milestone-payment-details", milestoneId],
    queryFn: () => thcApi.grantsApp.getMilestonePaymentDetails(milestoneId),
  });
};

export const useGetActionNeeded = () => {
  const { user } = useAuthentication();

  return useQuery({
    queryKey: ["action-needed"],
    queryFn: () => thcApi.grantsApp.actionNeeded(),
    enabled: !!user?.id,
  });
};
